import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import {
  DollarSign,
  ArrowLeft,
  Filter,
  ArrowUpRight,
  ArrowDownLeft,
  FileText,
  CreditCard,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

const TRANSACTION_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'trust_deposit', label: 'Trust Deposit' },
  { value: 'trust_withdrawal', label: 'Trust Withdrawal' },
  { value: 'trust_transfer', label: 'Trust Transfer' },
  { value: 'trust_refund', label: 'Trust Refund' },
  { value: 'trust_fee_earned', label: 'Trust Fee Earned' },
  { value: 'trust_interest', label: 'Trust Interest' },
  { value: 'trust_adjustment', label: 'Trust Adjustment' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'payment', label: 'Payment' },
];

const TYPE_STYLES = {
  trust_deposit: { icon: ArrowDownLeft, color: 'text-green-600', bg: 'bg-green-50', label: 'Trust Deposit' },
  trust_withdrawal: { icon: ArrowUpRight, color: 'text-red-600', bg: 'bg-red-50', label: 'Trust Withdrawal' },
  trust_transfer: { icon: RefreshCw, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Trust Transfer' },
  trust_refund: { icon: ArrowUpRight, color: 'text-orange-600', bg: 'bg-orange-50', label: 'Trust Refund' },
  trust_fee_earned: { icon: DollarSign, color: 'text-purple-600', bg: 'bg-purple-50', label: 'Fee Earned' },
  trust_interest: { icon: DollarSign, color: 'text-teal-600', bg: 'bg-teal-50', label: 'Interest' },
  trust_adjustment: { icon: RefreshCw, color: 'text-gray-600', bg: 'bg-gray-50', label: 'Adjustment' },
  invoice: { icon: FileText, color: 'text-yellow-600', bg: 'bg-yellow-50', label: 'Invoice' },
  payment: { icon: CreditCard, color: 'text-green-600', bg: 'bg-green-50', label: 'Payment' },
};

const PAGE_SIZE = 25;

const ClientTransactionHistory = () => {
  const { clientId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (typeFilter) params.transaction_type = typeFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const response = await api.get(`/billing/clients/${clientId}/transactions`, { params });
      setData(response.data);
      setPage(0);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [clientId, typeFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const transactions = data?.transactions || [];
  const summary = data?.summary || {};
  const clientName = data?.client?.name || 'Client';
  const totalPages = Math.ceil(transactions.length / PAGE_SIZE);
  const pageTransactions = transactions.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const formatCurrency = (val) => {
    const num = Number(val) || 0;
    return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return '-';
    return new Date(isoStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getTypeStyle = (type) => {
    return TYPE_STYLES[type] || { icon: DollarSign, color: 'text-gray-600', bg: 'bg-gray-50', label: type };
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Link to="/billing" className="inline-flex items-center text-blue-600 hover:text-blue-800">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Billing
        </Link>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link to="/billing" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-2">
          <ArrowLeft className="h-4 w-4 mr-1" /> Billing
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <DollarSign className="mr-3 h-8 w-8" />
          {clientName} &mdash; Transaction History
        </h1>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Trust Balance</p>
          <p className="text-xl font-bold text-gray-900">{formatCurrency(summary.trust_balance)}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Trust Deposits</p>
          <p className="text-xl font-bold text-green-600">{formatCurrency(summary.total_trust_deposits)}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Trust Withdrawals</p>
          <p className="text-xl font-bold text-red-600">{formatCurrency(summary.total_trust_withdrawals)}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Total Invoiced</p>
          <p className="text-xl font-bold text-yellow-600">{formatCurrency(summary.total_invoiced)}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Total Payments</p>
          <p className="text-xl font-bold text-green-600">{formatCurrency(summary.total_payments)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <Filter className="h-5 w-5 text-gray-400" />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
          >
            {TRANSACTION_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            placeholder="From"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            placeholder="To"
          />
          {(typeFilter || dateFrom || dateTo) && (
            <button
              onClick={() => { setTypeFilter(''); setDateFrom(''); setDateTo(''); }}
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {transactions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No transactions found{typeFilter || dateFrom || dateTo ? ' matching your filters' : ''}.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Case</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Trust Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {pageTransactions.map((txn) => {
                    const style = getTypeStyle(txn.transaction_type);
                    const Icon = style.icon;
                    return (
                      <tr key={txn.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {formatDate(txn.transaction_date)}
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${style.bg} ${style.color}`}>
                            <Icon className="h-3 w-3" />
                            {style.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate" title={txn.description}>
                          {txn.description}
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          {txn.case_id ? (
                            <Link to={`/cases/${txn.case_id}`} className="text-blue-600 hover:text-blue-800">
                              {txn.case_number || 'View Case'}
                            </Link>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium whitespace-nowrap">
                          <span className={
                            txn.transaction_type === 'payment' || txn.transaction_type === 'trust_deposit'
                              ? 'text-green-600'
                              : txn.transaction_type === 'trust_withdrawal' || txn.transaction_type === 'trust_refund'
                                ? 'text-red-600'
                                : 'text-gray-900'
                          }>
                            {formatCurrency(txn.amount)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            txn.status === 'completed' || txn.status === 'paid'
                              ? 'bg-green-100 text-green-800'
                              : txn.status === 'pending' || txn.status === 'sent' || txn.status === 'pending_approval'
                                ? 'bg-yellow-100 text-yellow-800'
                                : txn.status === 'draft'
                                  ? 'bg-gray-100 text-gray-800'
                                  : txn.status === 'overdue'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-700'
                          }`}>
                            {txn.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-500 whitespace-nowrap">
                          {txn.running_balance !== null ? formatCurrency(txn.running_balance) : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                <p className="text-sm text-gray-700">
                  Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, transactions.length)} of {transactions.length}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="p-1 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    className="p-1 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ClientTransactionHistory;
